import {
  ChevronDown,
  ChevronRight,
  CircleUser,
  Loader,
  RotateCcw,
  ShieldCheck,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { Fragment } from "react";

import {
  AppRegistrationStatusBadge,
  EnvironmentBadge,
  RequestTypeBadge,
} from "@/components/app-registration-badges";
import { PermissionDiff } from "@/components/app-registration-permission-change";
import { ScopeList, ScopeSummary } from "@/components/app-registration-scope-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AppRegistrationRequestDetail } from "@/lib/api";
import { formatDateTime, formatRelativeTime } from "@/lib/format";

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs tracking-wider uppercase">{label}</p>
      <div className="text-foreground mt-1 text-sm break-words">{children}</div>
    </div>
  );
}

const TRIAGE_LABEL: Record<string, string> = {
  app_registration: "App registration",
  managed_identity: "Managed identity",
};

const CREDENTIAL_LABEL: Record<string, string> = {
  federated: "Federated credential (no secret)",
  secret: "Client secret",
  none: "No credential",
};

interface RequestRowDetailProps {
  request: AppRegistrationRequestDetail;
  isOwnRequest: boolean;
  /** Why approving would collide with a decommission right now, if it would. */
  blockedReason: string | null;
  onApprove: (request: AppRegistrationRequestDetail) => void;
  onReject: (request: AppRegistrationRequestDetail) => void;
}

/**
 * Everything an approver needs that does not fit the collapsed row: the full
 * purpose, the credential being asked for, each permission with its risk and
 * consent type, and — for a `failed` row — why provisioning stopped.
 */
function RequestRowDetail({
  request,
  isOwnRequest,
  blockedReason,
  onApprove,
  onReject,
}: RequestRowDetailProps) {
  const scopeKeys = request.requested_scope_keys ?? [];
  const isRetry = request.status === "failed";
  // Approving this deletes real objects instead of creating them. It goes
  // through the same claim, the same retry and the same queue — but an
  // approver must never read "Approve & provision" on a row whose effect is
  // the opposite.
  const isDecommission = request.request_type === "delete";
  // A claimed row. The backend holds the claim for 15 minutes and only then
  // lets another approver take it over, so the honest thing to show is "someone
  // is already doing this" rather than a button that 409s.
  const isInFlight = request.status === "provisioning";
  const showReject = canReject(request);
  // Raised by this admin through Claude (MCP): allowed and recorded, but the
  // admin should check they actually asked for it before deciding.
  const raisedByYouThroughClaude = isOwnRequest && request.via === "mcp";

  return (
    <div className="bg-muted/30 space-y-5 px-4 py-4">
      {isInFlight && (
        <div className="border-border bg-background rounded-md border p-3">
          <p className="text-foreground flex items-center gap-2 text-sm font-medium">
            <Loader className="size-4 shrink-0 animate-spin" />
            {isDecommission
              ? "The decommission is already running"
              : "Provisioning is already running"}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            Another approver claimed this request and the Graph calls are in progress. It should
            finish or fail on its own within a minute. If it is still here after fifteen minutes the
            run died, and &ldquo;Take over provisioning&rdquo; below resumes it from where it
            stopped &mdash; before then the server will refuse, which is what stops two approvers
            creating two app registrations.
          </p>
        </div>
      )}

      {isRetry && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
          <p className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-300">
            <TriangleAlert className="size-4 shrink-0" />
            {isDecommission
              ? "Approved, but the decommission did not finish"
              : "Approved, but provisioning did not finish"}
          </p>
          <p className="mt-1 text-xs text-red-800/90 dark:text-red-300/90">
            {isDecommission
              ? decommissionHasStarted(request)
                ? "The approval still stands — approving again resumes the deletion from where it stopped; what is already gone counts as done. It cannot be rejected any more: that would not put anything back."
                : "The approval still stands — nothing has been deleted yet. Approve again to retry, or reject it if the reason above is not one a retry fixes."
              : "The approval still stands — approving again resumes provisioning from where it stopped, and will not create duplicate objects. No second decision is needed."}
          </p>
          {request.provisioning_error && (
            <pre className="text-foreground bg-background/70 mt-2 overflow-x-auto rounded p-2 text-xs whitespace-pre-wrap">
              {request.provisioning_error}
            </pre>
          )}
        </div>
      )}

      {request.sign_in_registration && (
        <div className="border-border bg-background flex items-start gap-2 rounded-md border p-3">
          <ShieldCheck className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <div>
            <p className="text-foreground text-sm font-medium">
              This changes the project&apos;s sign-in app registration
            </p>
            <p className="text-muted-foreground text-xs">
              Everyone who uses the app signs in with it. Once applied, added permissions go to the
              IT approval queue for a directory admin &mdash; there is no consent link to hand out
              here, and sign-in keeps working meanwhile.
            </p>
          </div>
        </div>
      )}

      {isDecommission && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
          <p className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-300">
            <Trash2 className="size-4 shrink-0" />
            Approving this deletes the app registration
          </p>
          <ul className="mt-1 ml-5 list-disc space-y-1 text-xs text-red-800/90 dark:text-red-300/90">
            <li>
              The application, its enterprise app and its federated credential are deleted from
              Entra, and the admin consent granted on it goes with them. The application is
              soft-deleted &mdash; the directory keeps it restorable for 30 days.
            </li>
            <li>
              Anything still configured with this client id loses delegated Microsoft Graph access
              immediately.
            </li>
            <li>
              Scaffold UI cannot edit the project&apos;s deployment. If{" "}
              <code className="font-mono">GRAPH_CLIENT_ID</code> is still set there, the requester
              has to remove it and redeploy.
            </li>
          </ul>
        </div>
      )}

      {(request.manual_follow_up ?? []).length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
          <p className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
            <TriangleAlert className="size-4 shrink-0" />
            Still to remove by hand
          </p>
          {/* The decommission refused to delete an object it could not prove
              was ours -- an enterprise application whose own application is
              already gone has no tag left to read. It keeps the delegated
              permissions a directory admin consented to, so it is stored on
              the row rather than only shown once on the result screen. */}
          <ul className="mt-1 ml-5 list-disc space-y-1 text-xs text-amber-900/90 dark:text-amber-200/90">
            {(request.manual_follow_up ?? []).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <DetailField label={isDecommission ? "Reason for decommissioning" : "Purpose"}>
        {request.purpose}
      </DetailField>

      <div className="grid gap-5 sm:grid-cols-2">
        <DetailField label="Application name">
          <code className="text-xs">{request.display_name}</code>
        </DetailField>
        <DetailField label="Environment">
          {request.environment ? (
            <span className="flex items-center gap-2">
              <EnvironmentBadge environment={request.environment} />
              <span className="text-muted-foreground text-xs">
                Consent granted here covers this environment only.
              </span>
            </span>
          ) : (
            // Predates per-environment registrations. Said plainly rather than
            // guessed: this one federates every environment the project had
            // deployed when it was approved.
            <span className="text-muted-foreground text-xs">
              Not recorded &mdash; predates per-environment registrations.
            </span>
          )}
        </DetailField>
        <DetailField label="Credential">
          Federated managed identity
          <p className="text-muted-foreground mt-1 text-xs">
            This project&apos;s own app identity, resolved from the project rather than named by the
            requester. No client secret is ever created.
          </p>
        </DetailField>
        {request.triage_verdict && (
          <DetailField label="Triage verdict">
            {TRIAGE_LABEL[request.triage_verdict] ?? request.triage_verdict}
            <p className="text-muted-foreground mt-1 text-xs">
              {/* Three-way, because the column is nullable: rendering a null as a
                  confident "no" would tell the approver the requester answered
                  something they never answered. */}
              Signs a person in and acts as them:{" "}
              {request.acts_as_signed_in_user === null ||
              request.acts_as_signed_in_user === undefined
                ? "not recorded"
                : request.acts_as_signed_in_user
                  ? "yes"
                  : "no"}
            </p>
          </DetailField>
        )}
        {request.entra_app_id && (
          <DetailField label="Application (client) ID">
            <code className="text-xs break-all">{request.entra_app_id}</code>
            <p className="text-muted-foreground mt-1 text-xs">
              The project&apos;s app authorises users directly against this registration.
            </p>
          </DetailField>
        )}
        {(isRetry || isInFlight) && request.approver_email && (
          <DetailField label="Approved by">
            {request.approver_email}
            {/* Self-approval is permitted (a single admin would otherwise be
                unable to request anything) but never silent -- the approver
                queue says so, and the activity log records it. */}
            {request.approver_email?.toLowerCase() === request.requester_email.toLowerCase() && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-950/60 dark:text-amber-300">
                approved their own request
              </span>
            )}
            <p className="text-muted-foreground mt-1 text-xs">
              {formatDateTime(request.decided_at ?? null)}
              {request.decision_note ? ` — ${request.decision_note}` : ""}
            </p>
          </DetailField>
        )}
        {request.entra_app_id && (
          <DetailField label="Already created">
            <code className="text-xs break-all">{request.entra_app_id}</code>
            <p className="text-muted-foreground mt-1 text-xs">
              Application (client) ID &mdash; a resume marker, not a finished app.
            </p>
          </DetailField>
        )}
      </div>

      {/* An amendment is a change, and the queue is where it is decided. The
          diff goes above the resulting set because the removals are the part
          an approver would otherwise never see -- they are absent from the
          list by definition. */}
      {request.request_type === "amend" && (
        <div>
          <p className="text-muted-foreground text-xs tracking-wider uppercase">What changes</p>
          <PermissionDiff
            base={request.base_scope_keys ?? []}
            requested={scopeKeys}
            className="mt-2"
          />
        </div>
      )}

      {/* A decommission asks for no permissions -- an empty "Requested
          permissions" block on it reads like a request for nothing rather
          than a request to take something away. */}
      {!isDecommission && (
        <div>
          <p className="text-muted-foreground text-xs tracking-wider uppercase">
            {request.request_type === "amend" ? "Resulting permissions" : "Requested permissions"}
          </p>
          <ScopeList scopeKeys={scopeKeys} className="mt-2" />
        </div>
      )}

      {/* Deciding your own request is allowed and recorded: the notice says what
          will be written down, and a request Claude raised gets a check-first one. */}
      {isOwnRequest && !raisedByYouThroughClaude && (
        <div className="border-border bg-background flex items-start gap-2 rounded-md border p-3">
          <CircleUser className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <div>
            <p className="text-foreground text-sm font-medium">This is your own request</p>
            <p className="text-muted-foreground text-xs">
              You can approve or reject it, and the decision is recorded as your own, on the request
              and in the activity log. Ask a second approver if one is available.
            </p>
          </div>
        </div>
      )}
      {raisedByYouThroughClaude && (
        <div className="border-border bg-background flex items-start gap-2 rounded-md border p-3">
          <CircleUser className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <div>
            <p className="text-foreground text-sm font-medium">
              You raised this through Claude (MCP)
            </p>
            <p className="text-muted-foreground text-xs">
              Check you actually asked Claude for this before approving it. You can approve or
              reject it; the decision is recorded as your own, together with the fact that Claude
              raised it.
            </p>
          </div>
        </div>
      )}
      {blockedReason && (
        <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span>{blockedReason}</span>
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => onApprove(request)}
          disabled={blockedReason !== null}
          variant={isDecommission ? "destructive" : isInFlight ? "outline" : "default"}
        >
          {isRetry || isInFlight ? (
            <>
              <RotateCcw />
              {isInFlight ? "Take over" : "Retry"}
              {isDecommission ? " decommission" : " provisioning"}
            </>
          ) : isDecommission ? (
            <>
              <Trash2 />
              Approve &amp; delete
            </>
          ) : (
            "Approve & provision"
          )}
        </Button>
        {showReject && (
          <Button variant="outline" onClick={() => onReject(request)}>
            Reject
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Whether the reject route would accept this decision, so the button is only
 * offered where it can succeed. Mirrors `reject_app_registration_request`:
 * `pending` and `failed` rows are rejectable -- a failed one has to be, or the
 * slot it holds is never freed. Rejecting your own request is allowed and
 * recorded, like approving it.
 *
 * A decommission is the exception once it has deleted something: refusing it
 * then would not put anything back, so only approving again (which finishes
 * it) is offered. One that failed before its first delete stays rejectable.
 */
function canReject(request: AppRegistrationRequestDetail): boolean {
  if (request.request_type === "delete" && decommissionHasStarted(request)) {
    return false;
  }
  return request.status === "pending" || request.status === "failed";
}

/** Whether a decommission's run has deleted anything yet (any step marker set). */
function decommissionHasStarted(request: AppRegistrationRequestDetail): boolean {
  return Boolean(
    request.deletion_started_at ||
    request.credentials_deleted_at ||
    request.service_principal_deleted_at ||
    request.application_deleted_at,
  );
}

/**
 * Why this row cannot be approved right now, if provisioning and a
 * decommission would otherwise run against one registration at once. Mirrors
 * the approve route (`_refuse_crossing_decommission`) from the queue the page
 * already holds.
 */
function crossingDecommission(
  request: AppRegistrationRequestDetail,
  requests: AppRegistrationRequestDetail[],
): string | null {
  if (
    request.request_type === "create" &&
    requests.some(
      (row) =>
        row.parent_request_id === request.id &&
        row.request_type === "delete" &&
        row.status !== "rejected",
    )
  ) {
    return "A decommission of this registration is open, so it is not provisioned again. Decide the decommission first.";
  }
  if (
    request.request_type === "delete" &&
    requests.some((row) => row.id === request.parent_request_id && row.status === "provisioning")
  ) {
    return "The registration this deletes is being provisioned right now. Approve once that run has finished or failed.";
  }
  return null;
}

interface RequestTableProps {
  requests: AppRegistrationRequestDetail[];
  /** The signed-in approver, used to flag (not hide) actions on their own requests. */
  currentUserEmail: string | null;
  expandedId: string | null;
  onToggleExpanded: (requestId: string) => void;
  onApprove: (request: AppRegistrationRequestDetail) => void;
  onReject: (request: AppRegistrationRequestDetail) => void;
}

export function RequestTable({
  requests,
  currentUserEmail,
  expandedId,
  onToggleExpanded,
  onApprove,
  onReject,
}: RequestTableProps) {
  if (requests.length === 0) {
    return (
      <div className="text-muted-foreground py-12 text-center text-sm">
        Nothing waiting on an approver.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Project</TableHead>
          <TableHead>Environment</TableHead>
          <TableHead>Requester</TableHead>
          <TableHead>Purpose</TableHead>
          <TableHead>Permissions</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Requested</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((request) => {
          const expanded = expandedId === request.id;
          const isOwnRequest =
            currentUserEmail !== null &&
            request.requester_email.toLowerCase() === currentUserEmail.toLowerCase();
          return (
            <Fragment key={request.id}>
              <TableRow className="cursor-pointer" onClick={() => onToggleExpanded(request.id)}>
                <TableCell className="text-muted-foreground align-top">
                  {/* The whole row toggles, but a real button keeps the detail
                      reachable from the keyboard and announces its state. */}
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExpanded(request.id);
                    }}
                    className="hover:text-foreground"
                  >
                    {expanded ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                    <span className="sr-only">
                      {expanded ? "Collapse" : "Expand"} request from {request.requester_email}
                    </span>
                  </button>
                </TableCell>
                <TableCell className="align-top">
                  <p className="text-foreground flex items-center gap-2 text-sm font-medium">
                    {request.project_name}
                    {request.sign_in_registration && (
                      <Badge
                        variant="secondary"
                        className="ml-2"
                        title="Everyone who uses the app signs in with this registration"
                      >
                        Sign-in
                      </Badge>
                    )}
                    <RequestTypeBadge requestType={request.request_type} />
                  </p>
                  <p className="text-muted-foreground truncate text-xs">{request.display_name}</p>
                </TableCell>
                <TableCell className="align-top">
                  <EnvironmentBadge environment={request.environment} />
                </TableCell>
                <TableCell className="align-top">
                  <p className="text-foreground text-sm">{request.requester_email}</p>
                  {request.via === "mcp" && (
                    <p className="text-muted-foreground text-xs">via Claude (MCP)</p>
                  )}
                  {isOwnRequest && (
                    <p className="text-muted-foreground text-xs">
                      You &mdash; a self-decision is recorded
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground max-w-xs align-top text-sm">
                  <span className="line-clamp-2">{request.purpose}</span>
                </TableCell>
                <TableCell className="align-top">
                  <ScopeSummary scopeKeys={request.requested_scope_keys ?? []} />
                </TableCell>
                <TableCell className="align-top">
                  <AppRegistrationStatusBadge
                    status={request.status}
                    requestType={request.request_type}
                    decommissionedAt={request.decommissioned_at}
                  />
                </TableCell>
                <TableCell className="text-muted-foreground align-top text-sm">
                  {formatRelativeTime(request.created_at)}
                </TableCell>
              </TableRow>
              {expanded && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={8} className="p-0">
                    <RequestRowDetail
                      request={request}
                      isOwnRequest={isOwnRequest}
                      blockedReason={crossingDecommission(request, requests)}
                      onApprove={onApprove}
                      onReject={onReject}
                    />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
